import React, { useMemo, useState } from 'react';
import { useStore } from '../store/StoreContext';
import { calculateSchedule, getStatusColor } from '../utils/helpers';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { ItemEditorModal } from '../components/UI/ItemEditorModal';
import { Select } from '../components/UI/Input';

const ROW_HEIGHT = 56;
const DAY_WIDTH = 40;

export const GanttView = () => {
  const { state } = useStore();
  const [expandedProjects, setExpandedProjects] = useState({});
  const [editingItem, setEditingItem] = useState(null);
  const [selectedBU, setSelectedBU] = useState('all');

  const { scheduledProjects, minDate, totalDays } = useMemo(() => {
    // 1. Filter projects by Business Unit
    let projects = Object.values(state.projects || {});
    if (selectedBU !== 'all') {
      projects = projects.filter(p => p.businessUnitId === selectedBU);
    }

    // 2. Calculate schedules for filtered projects
    const scheduled = projects.map(p => calculateSchedule(p));

    if (scheduled.length === 0) {
      return { scheduledProjects: [], totalDays: 0, minDate: new Date() };
    }

    // 3. Find global date range for the timeline
    let earliest = new Date('2099-01-01');
    let latest = new Date('2000-01-01');

    scheduled.forEach(p => {
      const pStart = new Date(p.startDate + 'T00:00:00');
      const pEnd = new Date(p.calculatedEndDate + 'T00:00:00');
      if (pStart < earliest) earliest = pStart;
      if (pEnd > latest) latest = pEnd;
    });

    // Add padding (3 days before, 14 days after)
    earliest = new Date(earliest.setDate(earliest.getDate() - 3));
    latest = new Date(latest.setDate(latest.getDate() + 14));

    const diffDays = Math.ceil(Math.abs(latest - earliest) / (1000 * 60 * 60 * 24));

    return { 
      scheduledProjects: scheduled, 
      minDate: earliest, 
      totalDays: diffDays 
    };
  }, [state.projects, selectedBU]);

  const getX = (dateStr) => {
    if (!dateStr) return 0;
    const date = new Date(dateStr + 'T00:00:00');
    return Math.floor((date - minDate) / (1000 * 60 * 60 * 24)) * DAY_WIDTH;
  };

  const days = Array.from({ length: totalDays }, (_, i) => {
    const d = new Date(minDate);
    d.setDate(d.getDate() + i);
    return d;
  });

  let currentY = ROW_HEIGHT;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-full flex flex-col bg-base-900 overflow-hidden">
      <div className="p-6 border-b border-base-700 bg-base-800 flex justify-between items-center shrink-0">
        <div>
          <h1 className="text-2xl font-bold text-white">Timeline View</h1>
          <p className="text-sm text-gray-400">Project sequence and workflow dependencies.</p>
        </div>
        
        <div className="w-64">
          <Select 
            label="Filter by Business Unit" 
            value={selectedBU} 
            onChange={(e) => setSelectedBU(e.target.value)}
            options={[
              { label: 'All Business Units', value: 'all' },
              ...Object.values(state.businessUnits || {}).map(bu => ({
                label: bu.name,
                value: bu.id
              }))
            ]}
          />
        </div>
      </div>

      <div className="flex-1 overflow-auto custom-scrollbar bg-gantt-pattern">
        {scheduledProjects.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-gray-500 opacity-40">
            <SafeIcon name="AlignLeft" className="w-16 h-16 mb-4" />
            <p className="text-lg font-medium">No projects found for this selection.</p>
          </div>
        ) : (
          <div style={{ width: `${totalDays * DAY_WIDTH}px`, minHeight: '100%' }} className="relative">
            <div className="sticky top-0 h-[48px] bg-base-900/95 backdrop-blur border-b border-base-700 flex z-40">
              {days.map((d, i) => (
                <div key={i} className="flex-shrink-0 border-r border-base-800 flex flex-col items-center justify-center text-[10px]" style={{ width: DAY_WIDTH }}>
                  <span className="text-gray-500">{d.toLocaleDateString('en-US', { weekday: 'short' })}</span>
                  <span className="font-mono text-gray-300">{d.getDate()}</span>
                </div>
              ))}
            </div>

            <div className="relative">
              {scheduledProjects.map((project) => {
                const pStartX = getX(project.startDate);
                const pEndX = getX(project.calculatedEndDate);
                const pWidth = Math.max(DAY_WIDTH, (pEndX + DAY_WIDTH) - pStartX);
                const isExpanded = expandedProjects[project.id];
                const pRowTop = currentY;
                const projectColor = project.color || '#6C63FF';
                currentY += ROW_HEIGHT;

                return (
                  <React.Fragment key={project.id}>
                    <div className="absolute border-b border-base-800/50 w-full hover:bg-base-800/30 transition-colors" style={{ top: pRowTop, height: ROW_HEIGHT }}>
                      <div 
                        onClick={() => setExpandedProjects(prev => ({ ...prev, [project.id]: !isExpanded }))}
                        className="absolute h-10 rounded-md shadow-lg cursor-pointer flex items-center px-4 text-xs font-semibold text-white transition-all hover:brightness-110"
                        style={{ left: pStartX, width: pWidth, top: 8, backgroundColor: projectColor }}
                      >
                        <SafeIcon name={isExpanded ? 'ChevronDown' : 'ChevronRight'} className="mr-2 w-4 h-4" />
                        {project.name}
                      </div>
                    </div>

                    {isExpanded && project.scheduledWorkflows.map((wf, wfIdx) => {
                      const wStartX = getX(wf.startDate);
                      const wEndX = getX(wf.endDate);
                      const wWidth = Math.max(DAY_WIDTH, (wEndX + DAY_WIDTH) - wStartX);
                      const wRowTop = currentY;
                      currentY += ROW_HEIGHT;

                      return (
                        <React.Fragment key={wf.id + wfIdx}>
                          <div className="absolute border-b border-base-800/20 w-full" style={{ top: wRowTop, height: ROW_HEIGHT }}>
                            <button 
                              onClick={() => setEditingItem({ item: wf, projectId: project.id, type: 'workflow' })}
                              className="absolute h-8 rounded border border-white/10 flex items-center px-3 text-[10px] text-white hover:bg-white/10 transition-colors shadow-sm"
                              style={{ left: wStartX, width: wWidth, top: 10, backgroundColor: `${projectColor}40` }}
                            >
                              <div className="w-2.5 h-2.5 rounded-full mr-2.5 shrink-0 shadow-sm" style={{ backgroundColor: getStatusColor(wf.calculatedStatus) }}></div>
                              <span className="truncate">{wf.name}</span>
                            </button>
                          </div>

                          {wf.scheduledTasks.map((task, tIdx) => {
                            const tStartX = getX(task.startDate);
                            const tEndX = getX(task.endDate);
                            const tWidth = Math.max(DAY_WIDTH, (tEndX + DAY_WIDTH) - tStartX);
                            const tRowTop = currentY;
                            currentY += ROW_HEIGHT;

                            return (
                              <div key={task.id + tIdx} className="absolute border-b border-base-800/10 w-full" style={{ top: tRowTop, height: ROW_HEIGHT }}>
                                <button 
                                  onClick={() => setEditingItem({ item: task, projectId: project.id, type: 'task' })}
                                  className="absolute h-6 rounded-sm bg-base-800 border border-base-700 flex items-center px-2 text-[9px] text-gray-400 hover:border-primary transition-all shadow-sm"
                                  style={{ left: tStartX, width: tWidth, top: 11 }}
                                >
                                  <div className="w-2 h-2 rounded-full mr-2 shrink-0" style={{ backgroundColor: getStatusColor(task.status) }}></div>
                                  <span className="truncate">{task.name}</span>
                                </button>
                              </div>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <ItemEditorModal 
        isOpen={!!editingItem} 
        onClose={() => setEditingItem(null)} 
        {...editingItem} 
      />
    </motion.div>
  );
};